import { inject, Injectable, Injector } from '@angular/core'
import { Router } from '@angular/router'
import { UserStore } from './user.store'
import { UserService } from '../../services/user.service'
import { DoctorService } from '../../services/doctor.service'
import { OperatorService } from '../../services/operator.service'
import { removeStorageItem, setStorageItem } from '../../common/utils/storage.utils'
import { UserType } from '../../common/constants'
import { tap, switchMap, EMPTY, catchError, finalize, of, timeout, exhaustMap, forkJoin, map } from 'rxjs'
import { BaseEffects } from '../base-store/base.effects'
import { UserState } from './user.state'
import { RoleStore } from '../roles/roles.store'
import { Role } from '../interfaces/role'
import { ApiService } from '../../services/api.service'
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request'
import { StoreResetService } from '../../services/store-reset.service'
import { NotificationsWsService } from '../../services/notifications-ws.service'
import { Doctor } from '../interfaces/doctor'
import { Operator } from '../interfaces/operator'
import { ContractDoctorChannelService } from '../../services/contract-doctor-channel.service'
import { ContractDoctorSpecialtyService } from '../../services/contract-doctor-specialty.service'
import { PermissionService } from '../../permissions/permission.service'
import { validateLegacyRoleMapping } from '../../permissions/legacy/legacy-role.validation'
import { environment } from '../../../environments/environment'
import { resolvePortalSubdomain } from '../../common/utils/portal-subdomain.util'
import { SessionWarmService } from '../../services/session-warm.service'

@Injectable({ providedIn: 'root' })
export class UserEffects extends BaseEffects<UserState> {
  private injector = inject(Injector)
  private store = inject(UserStore)
  private userService = inject(UserService)
  private doctorService = inject(DoctorService)
  private operatorService = inject(OperatorService)
  private apiService = inject(ApiService)
  private storeReset = inject(StoreResetService)
  private roleStore = inject(RoleStore)
  private contractDoctorChannelService = inject(ContractDoctorChannelService)
  private contractDoctorSpecialtyService = inject(ContractDoctorSpecialtyService)

  private get notificationsWs(): NotificationsWsService {
    return this.injector.get(NotificationsWsService)
  }

  private get router(): Router {
    return this.injector.get(Router)
  }

  private get permissionService(): PermissionService {
    return this.injector.get(PermissionService)
  }

  private get sessionWarmService(): SessionWarmService {
    return this.injector.get(SessionWarmService)
  }

  readonly login = this.store.effect<{ username: string; password: string }>((login$) =>
    login$.pipe(
      tap(() => {
        this.store.setLoading(true)
        this.store.setError(null)
      }),
      switchMap(({ username, password }) =>
        this.userService.loginApi(username, password).pipe(
          switchMap((response) => this.handleLoginResponse(response, username)),
          catchError((err) => {
            const backendError = err?.error ?? {}
            const backendMessage: string = backendError?.message ?? err?.message ?? ''
            if (
              err?.status === 429 ||
              backendError?.loginBlocked ||
              backendMessage.includes('too many failed attempts')
            ) {
              this.store.setError('login_blocked')
              return EMPTY
            }
            if (backendError?.passwordExpired) {
              return this.handleLoginResponse(backendError, username)
            }
            if (backendError?.code === 'account_pending' || backendError?.accountPending) {
              this.store.setError('account_pending')
            } else if (backendError?.code === 'contact_admin' || backendError?.contactAdmin) {
              this.store.setError('contact_admin')
            } else if (err?.status === 401) {
              this.store.setError('invalid_credentials')
            } else {
              this.store.setError('login_error')
            }
            return EMPTY
          }),
        ),
      ),
      finalize(() => this.store.setLoading(false)),
    ),
  )

  readonly changeExpiredPassword = this.store.effect<{
    email: string
    currentPassword: string
    newPassword: string
  }>((payload$) =>
    payload$.pipe(
      tap(() => {
        this.store.setLoading(true)
        this.store.setError(null)
      }),
      switchMap(({ email, currentPassword, newPassword }) =>
        this.userService.changeExpiredPasswordApi(email, currentPassword, newPassword).pipe(
          tap(() => {
            this.store.clearPasswordExpiredChallenge()
            this.store.setError(null)
          }),
          switchMap(() => this.userService.loginApi(email, newPassword)),
          switchMap((response) => this.handleLoginResponse(response, email)),
          catchError((err) => {
            this.store.setError(this.resolveExpiredPasswordError(err))
            return EMPTY
          }),
        ),
      ),
      finalize(() => this.store.setLoading(false)),
    ),
  )

  readonly verifyMfa = this.store.effect<{ mfaToken: string; code: string }>((verify$) =>
    verify$.pipe(
      tap(() => this.store.setLoading(true)),
      switchMap(({ mfaToken, code }) =>
        this.userService.verifyMfaApi(mfaToken, code).pipe(
          switchMap((response) => {
            this.store.clearMfaChallenge()
            return this.completeLogin(response)
          }),
          catchError(() => {
            this.store.setError('mfa_verify_error')
            return EMPTY
          }),
        ),
      ),
      finalize(() => this.store.setLoading(false)),
    ),
  )

  private handleLoginResponse(response: any, fallbackEmail?: string) {
    if (response?.loginBlocked) {
      this.store.setError('login_blocked')
      return EMPTY
    }
    if (response?.passwordExpired) {
      this.store.setPasswordExpiredChallenge(response.email ?? fallbackEmail ?? '')
      return EMPTY
    }
    if (response?.accountPending) {
      this.store.setError('account_pending')
      return EMPTY
    }
    if (response?.contactAdmin) {
      this.store.setError('contact_admin')
      return EMPTY
    }
    if (response?.mfaRequired && response?.mfaToken) {
      this.store.setMfaChallenge({ mfaToken: response.mfaToken })
      return EMPTY
    }
    if (!response?.sessionToken) {
      this.store.setError('invalid_credentials')
      return EMPTY
    }
    return this.completeLogin(response)
  }

  private resolveExpiredPasswordError(err: any): string {
    const backendMessage = String(err?.error?.message ?? err?.message ?? '').toLowerCase()

    if (err?.status === 401 || backendMessage.includes('invalid credentials')) {
      return 'invalid_credentials'
    }
    if (backendMessage.includes('security requirements') || backendMessage.includes('min 8 chars')) {
      return 'password_complexity'
    }
    if (backendMessage.includes('change not required')) {
      return 'password_change_not_required'
    }
    return 'login_error'
  }

  private completeLogin(response: any) {
    if (!response.user || !response.sessionToken) {
      throw new Error('Invalid credentials')
    }
    this.store.setToken(response.sessionToken)
    this.store.setRefreshToken(response.refreshToken ?? null)
    if (response.csrfToken) {
      this.store.setCsrfToken(response.csrfToken)
      setStorageItem('auth_csrf', response.csrfToken)
    }

    return forkJoin({
      profile: this.userService.getProfile(),
      warm: this.userService.warmSessionCache().pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ profile }) =>
        this.sessionWarmService.warmStableReferenceData(profile).pipe(
          catchError(() => of(undefined)),
          map(() => profile),
        ),
      ),
      tap((profile) => {
        if (!profile) throw new Error('Error loading profile')
        this.sessionWarmService.startPeriodicScopeWarm(profile)
        if (response.user.userType === UserType.DOCTOR) {
          this.onDoctorLoaded(profile as Doctor, response)
          return
        }
        if (response.user.userType === UserType.OPERATOR) {
          this.onOperatorLoaded(profile as Operator, response)
        }
      }),
    )
  }

  readonly logout = this.store.effect((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.sessionWarmService.stopPeriodicScopeWarm()
        this.clearAuthStorage()
        this.store.clearMfaChallenge()
        this.storeReset.resetAll()
        this.notificationsWs.disconnect()
        this.router.navigate(['/login'])
      }),
    ),
  )

  readonly updateProfile = this.store.effect<any>((payload$) =>
    payload$.pipe(
      tap(() => this.store.setLoading(true)),
      switchMap((formValue) => {
        const doctor = this.store.user() as Doctor
        if (!doctor?.id) {
          this.store.setLoading(false)
          this.store.setError('No se encontró el ID del médico')
          return EMPTY
        }

        return this.userService.updateProfileMe(formValue).pipe(
          switchMap((updatedUser) => {
            if (!updatedUser) throw new Error('Error updating profile')
            // El endpoint devuelve el SafeUserBase con doctor anidado
            const updatedDoctor = (updatedUser as any).doctor ?? doctor
            const merged: Doctor = {
              ...updatedDoctor,
              userBase: {
                ...(updatedDoctor.userBase ?? {}),
                timezone: updatedDoctor.userBase?.timezone ?? formValue.timezone,
                preferredLanguage: updatedDoctor.userBase?.preferredLanguage ?? formValue.preferredLanguage,
                email: updatedDoctor.userBase?.email ?? formValue.email,
              },
            }
            this.store.setUser(merged)
            setStorageItem('auth_user', JSON.stringify(merged))
            return of(null)
          }),
          catchError((err) => {
            this.store.setError(err.message ?? 'Error al guardar el perfil')
            return EMPTY
          }),
          finalize(() => this.store.setLoading(false)),
        )
      }),
    ),
  )

  readonly getProfile = this.store.effect((trigger$) =>
    trigger$.pipe(
      tap(() => this.store.setLoading(true)),
      exhaustMap(() =>
        this.userService.getProfile().pipe(
          timeout(15000),
          switchMap((response) => {
            if (!response) throw new Error('Error getting profile')
            const current = this.store.user()
            if (response && 'isActiveMedical' in response && current && 'isActiveMedical' in current) {
              const legacyProfile = environment.profileApiModel === 'legacy'
              const mergedDoctor: Doctor = {
                ...current,
                ...response,
                id: legacyProfile
                  ? String((response as Doctor).id ?? (current as Doctor).id ?? '')
                  : (() => {
                      const responseDoctorId = Number((response as any).id)
                      const currentDoctorId = Number((current as any).id)
                      return Number.isFinite(responseDoctorId)
                        ? responseDoctorId
                        : Number.isFinite(currentDoctorId)
                          ? currentDoctorId
                          : (current as any).id
                    })(),
                channels: response.channels?.length ? response.channels : (current.channels ?? []),
                specialties: response.specialties?.length ? response.specialties : (current.specialties ?? []),
                contractDoctors: response.contractDoctors?.length
                  ? response.contractDoctors
                  : (current.contractDoctors ?? []),
                headContracts: response.headContracts?.length ? response.headContracts : (current.headContracts ?? []),
                doctorRoles: response.doctorRoles?.length ? response.doctorRoles : (current.doctorRoles ?? []),
              }
              return this.hydrateDoctorRelationsIfMissing(mergedDoctor).pipe(
                tap((doctor) => this.persistSessionUser(doctor)),
                switchMap(() => of(null)),
              )
            } else {
              this.persistSessionUser(response)
              return of(null)
            }
          }),
          tap(() => this.sessionWarmService.startPeriodicScopeWarm(this.store.user())),
          catchError((err) => {
            // Priorizar el mensaje del backend (err.error.message) sobre el genérico de HttpClient
            const backendMessage: string = err?.error?.message ?? err?.message ?? 'Unknown error'
            this.store.setError(backendMessage)
            return EMPTY
          }),
        ),
      ),
      finalize(() => this.store.setLoading(false)),
    ),
  )

  readonly redirectAfterLogin = () => {
    const user = this.store.user()
    if (!user) return

    if (
      environment.profileApiModel === 'legacy' &&
      'mustSetPasswordNextLogin' in user &&
      user.mustSetPasswordNextLogin === true
    ) {
      void this.router.navigateByUrl('/completar-registro')
      return
    }

    if (environment.profileApiModel === 'legacy' && 'termsAccepted' in user && user.termsAccepted === false) {
      void this.router.navigateByUrl('/aceptar-terminos')
      return
    }

    void this.router.navigateByUrl(this.permissionService.getDefaultRoute())
  }

  private validateLegacyRoles(roleIds: number[], roles: Role[]): void {
    if (environment.profileApiModel !== 'legacy') return

    const result = validateLegacyRoleMapping(roleIds, roles)
    if (!result.valid && environment.features.enableLogging) {
      console.warn('[PermissionService] Legacy role validation failed', result)
    }
  }

  private onOperatorLoaded(operator: Operator | null, response: any) {
    if (!operator) throw new Error('Error Loading Operator')
    if (operator?.operatorRoles?.length) {
      const roleIds = operator.operatorRoles.map((dr) => dr.roleId)
      const query = RequestQueryBuilder.create({ search: { id: { [CondOperator.IN]: roleIds } } })
      this.apiService.getAll<Role>('roles', query).subscribe((roles) => {
        const entities = roles ?? []
        this.roleStore.setRoles(entities)
        this.validateLegacyRoles(roleIds, entities)
      })
    }
    this.store.setUser(operator)
    this.saveToStorage(operator, response.sessionToken, response.refreshToken, response.csrfToken)
    this.connectWs(response.sessionToken)
    this.redirectAfterLogin()
  }

  private onDoctorLoaded(doctor: Doctor | null, response: any) {
    if (!doctor) throw new Error('Error Loading Doctor')
    if (doctor?.doctorRoles?.length) {
      const roleIds = doctor.doctorRoles.map((dr) => dr.roleId)
      const query = RequestQueryBuilder.create({ search: { id: { [CondOperator.IN]: roleIds } } })
      this.apiService.getAll<Role>('roles', query).subscribe((roles) => {
        const entities = roles ?? []
        this.roleStore.setRoles(entities)
        this.validateLegacyRoles(roleIds, entities)
      })
    }
    this.store.setUser(doctor)
    this.saveToStorage(doctor, response.sessionToken, response.refreshToken, response.csrfToken)
    this.connectWs(response.sessionToken)
    this.redirectAfterLogin()
  }

  private persistSessionUser(user: UserState['user']): void {
    if (!user) return
    this.store.setUser(user)
    setStorageItem('auth_user', JSON.stringify(user))
  }

  private saveToStorage(user: any, token: string, refreshToken?: string, csrfToken?: string) {
    setStorageItem('auth_user', JSON.stringify(user))
    setStorageItem('auth_token', token)
    if (refreshToken) setStorageItem('auth_refresh', refreshToken)
    if (csrfToken) setStorageItem('auth_csrf', csrfToken)
  }

  private connectWs(token: string): void {
    this.notificationsWs.connect(token, resolvePortalSubdomain())
  }

  private clearAuthStorage() {
    removeStorageItem('auth_user')
    removeStorageItem('auth_token')
    removeStorageItem('auth_refresh')
    removeStorageItem('auth_csrf')
  }

  private hydrateDoctorRelationsIfMissing(doctor: Doctor) {
    const hasChannels = (doctor.channels?.length ?? 0) > 0
    const hasSpecialties = (doctor.specialties?.length ?? 0) > 0
    if (hasChannels && hasSpecialties) return of(doctor)
    const directContractDoctorIds = (doctor.contractDoctors ?? [])
      .map((cd: any) => cd?.id)
      .filter((id: unknown) => id != null && String(id).trim() !== '')

    const contractDoctorIds$ = directContractDoctorIds.length ? of(directContractDoctorIds) : of([])

    return contractDoctorIds$.pipe(
      switchMap((contractDoctorIds: Array<number | string>) => {
        if (!contractDoctorIds.length) return of(doctor)
        const limitedContractDoctorIds = contractDoctorIds.slice(0, 50)

        const channelRequests = limitedContractDoctorIds.map((id) =>
          this.contractDoctorChannelService.getByContractDoctor(id).pipe(
            timeout(4000),
            catchError(() => of([])),
          ),
        )
        const specialtyRequests = limitedContractDoctorIds.map((id) =>
          this.contractDoctorSpecialtyService.getByContractDoctor(id).pipe(
            timeout(4000),
            catchError(() => of([])),
          ),
        )

        return forkJoin({
          channelsByContract: channelRequests.length ? forkJoin(channelRequests) : of([]),
          specialtiesByContract: specialtyRequests.length ? forkJoin(specialtyRequests) : of([]),
        }).pipe(
          map(({ channelsByContract, specialtiesByContract }) => {
            const channelIds = Array.from(
              new Set(
                channelsByContract.flatMap((records: any[]) =>
                  records.map((r: any) => String(r.channelId || '')).filter(Boolean),
                ),
              ),
            )
            const specialtyIds = Array.from(
              new Set(
                specialtiesByContract.flatMap((records: any[]) =>
                  records.map((r: any) => String(r.specialtyId || '')).filter(Boolean),
                ),
              ),
            )

            return {
              ...doctor,
              channels: hasChannels
                ? doctor.channels
                : channelIds.map((channelId) => ({ doctorId: doctor.id, channelId })),
              specialties: hasSpecialties
                ? doctor.specialties
                : specialtyIds.map((specialtyId) => ({ doctorId: doctor.id, specialtyId })),
            } as Doctor
          }),
        )
      }),
    )
  }
}
